/**
 * Trend suffix set per widget kind, in PLOT order. The backend's channel store
 * (lib_tiger_rs/src/channel.rs:1006-1023) defines WHICH suffixes exist per
 * channel type — analog channels have (AVG)/(MAX)/(MIN), digital channels have
 * (MAX)/(MIN) — and tiger_func uniquely identifies the channel type for a tag
 * widget. The ORDER below is the preferred display/plot order — (MAX) then (AVG)
 * then (MIN) for analog — and is independent of the store's internal order.
 *
 * Add an entry here when wiring the right-click menu for a new widget kind.
 */
export const ANALOG_SUFFIXES = ["(MAX)", "(AVG)", "(MIN)"] as const;
export const DIGITAL_SUFFIXES = ["(MAX)", "(MIN)"] as const;

export const TREND_SUFFIXES_BY_FUNC: Record<string, readonly string[]> = {
  // Analog channels — (MAX)/(AVG)/(MIN) plot order
  ANALOG: ANALOG_SUFFIXES,
  ANALOG_BOX: ANALOG_SUFFIXES,
  BARCHART: ANALOG_SUFFIXES,
  HORIZ_BARCHART: ANALOG_SUFFIXES,
  STACK_BARCHART: ANALOG_SUFFIXES,
  EXHAUST_PATTERN: ANALOG_SUFFIXES,
  EXHAUST_SPREAD: ANALOG_SUFFIXES,
  EXHAUST_ARC: ANALOG_SUFFIXES,
  TRIANGLE_VALUE: ANALOG_SUFFIXES,
  FLAME_INTENSITY: ANALOG_SUFFIXES,
  // Digital channels — MAX/MIN only
  DIGITAL: DIGITAL_SUFFIXES,
  DIGITALSTATUS: DIGITAL_SUFFIXES,
  DIGITALTEXT: DIGITAL_SUFFIXES,
  DIGITAL_BOX: DIGITAL_SUFFIXES,
  DIGITAL_BITMAP: DIGITAL_SUFFIXES,
  RECTANGLE: DIGITAL_SUFFIXES,
  CIRCLE: DIGITAL_SUFFIXES,
  TRIANGLE: DIGITAL_SUFFIXES,
};

export function getTrendSuffixes(tigerFunc: string | null | undefined): readonly string[] {
  if (!tigerFunc) return [];
  return TREND_SUFFIXES_BY_FUNC[tigerFunc] ?? [];
}

/**
 * Split a `graphable_tags` field (pipe-separated `config:tag` entries) into
 * an array of fully-qualified tag strings. Empty segments are filtered out.
 */
export function parseGraphableTags(s: string | null | undefined): string[] {
  if (!s) return [];
  return s.split("|").filter(t => t.length > 0);
}

/** Minimal view-element shape needed to classify a widget's tags by type. */
export interface BucketableElement {
  tiger_func: string | null | undefined;
  graphable_tags?: string | null | undefined;
}

/**
 * Bucket every tag in a view into analog vs digital, deduped (first-seen order
 * preserved). Mirrors the C++ `OnDisplayGraphSetButton`/`OnDisplayTrendSetButton`
 * split, which sorts each tag into an analog or digital list by its channel
 * type. We approximate the channel type with `tiger_func` via getTrendSuffixes:
 * a 3-suffix (AVG/MAX/MIN) func is analog, a 2-suffix (MAX/MIN) func is digital.
 * Widgets with no graphable tags or an unclassifiable func are skipped.
 */
/**
 * Group fully-qualified `config:tag` entries by their config (turbine),
 * preserving first-seen order of both configs and tags. Used to open one
 * graph/trend-set window per turbine instead of mixing turbines in one window.
 */
export function splitTagsByConfig(tags: readonly string[]): string[][] {
  const order: string[] = [];
  const byConfig = new Map<string, string[]>();
  for (const t of tags) {
    const config = t.slice(0, t.indexOf(":") + 1);
    if (!byConfig.has(config)) {
      byConfig.set(config, []);
      order.push(config);
    }
    byConfig.get(config)!.push(t);
  }
  return order.map((c) => byConfig.get(c)!);
}

export function bucketViewTags(
  elements: readonly BucketableElement[],
): { analog: string[]; digital: string[] } {
  const analog = new Set<string>();
  const digital = new Set<string>();
  for (const el of elements) {
    const tags = parseGraphableTags(el.graphable_tags);
    if (tags.length === 0) continue;
    const suffixes = getTrendSuffixes(el.tiger_func);
    const bucket =
      suffixes.length === ANALOG_SUFFIXES.length ? analog :
      suffixes.length === DIGITAL_SUFFIXES.length ? digital :
      null;
    if (!bucket) continue;
    for (const t of tags) bucket.add(t);
  }
  return { analog: [...analog], digital: [...digital] };
}
